import React from 'react';
import { IExerciseSetConfiguration } from '../../../types/workout/exercise-set-configuration';
import { IExerciseSetLog } from '../../../types/workout/exercise-set-log';
import { PD, SF, SFm } from './tokens';
import RpeChip from './RpeChip';
import { nonEmpty } from './helpers';

type Row = IExerciseSetConfiguration | IExerciseSetLog;

interface CompactSetTableProps {
  rows: Row[];
  /** True for `setConfiguration` (plan), false for `setLogs` (real). */
  plan?: boolean;
  /** Label for the `rating` column when plan === false. */
  rpeLabel?: string;
}

const PLAN_KEYS = [
  'repetitions',
  'weight',
  'time',
  'tempo',
  'restInterval',
  'distance',
  'difficulty',
  'duration'
] as const;
const REAL_KEYS = [
  'repetitions',
  'weight',
  'time',
  'tempo',
  'restInterval',
  'distance',
  'difficulty',
  'duration',
  'rating'
] as const;

const LABELS: Record<string, string> = {
  repetitions: 'Reps',
  weight: 'Peso',
  time: 'Tiempo',
  tempo: 'Tempo',
  restInterval: 'Rest',
  distance: 'Dist.',
  difficulty: 'Dif.',
  duration: 'Dur.',
  rating: 'RPE'
};

const CompactSetTable: React.FC<CompactSetTableProps> = ({ rows, plan = true, rpeLabel }) => {
  if (!rows || rows.length === 0) return null;

  const possibleKeys = plan ? PLAN_KEYS : REAL_KEYS;
  const cols = possibleKeys.filter((k) => rows.some((r) => nonEmpty((r as unknown as Record<string, unknown>)[k])));
  if (cols.length === 0) return null;

  const labels: Record<string, string> = { ...LABELS, rating: rpeLabel || 'RPE' };
  const gridCols = `22px repeat(${cols.length}, 1fr)`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: 6,
          alignItems: 'center',
          padding: '0 2px 4px',
          fontFamily: SF,
          fontSize: 9.5,
          fontWeight: 700,
          color: PD.ink3,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          borderBottom: `0.5px solid ${PD.divider}`
        }}
      >
        <span />
        {cols.map((c) => (
          <span key={c}>{labels[c]}</span>
        ))}
      </div>
      {rows.map((r, i) => {
        const rec = r as unknown as Record<string, unknown>;
        return (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              gap: 6,
              alignItems: 'center',
              padding: '3px 2px',
              fontFamily: SFm,
              fontSize: 12,
              fontWeight: 500,
              color: PD.ink
            }}
          >
            <span style={{ color: PD.ink3, fontWeight: 600 }}>{(rec.setNumber as number) ?? ''}</span>
            {cols.map((c) => {
              const v = rec[c];
              if (c === 'rating' && nonEmpty(v)) {
                return (
                  <span key={c}>
                    <RpeChip value={v as string | number} />
                  </span>
                );
              }
              return <span key={c}>{nonEmpty(v) ? String(v) : <span style={{ color: PD.ink3 }}>—</span>}</span>;
            })}
          </div>
        );
      })}
    </div>
  );
};

export default CompactSetTable;
